import { debounce, throttle } from 'lodash';
import tippy, { roundArrow } from 'tippy.js';
import Vue from 'vue';

var colors = {
  /**
   * Transform an hex color to and object containing the rgb
   * @param {String} hex
   * @return {Object} { r, g, b }
   */
  hexToRgb(hex) {
    let value = hex; // #000

    if (hex.length === 4) {
      const [, r, g, b] = hex;
      value = `${r}${r}${g}${g}${b}${b}`;
    }

    const exp = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})/i;
    const [, r, g, b] = exp.exec(value);
    return {
      r: parseInt(r, 16),
      g: parseInt(g, 16),
      b: parseInt(b, 16)
    };
  },

  /**
   * Retrieve the color in decimal units
   * @param {String} color Color string to parse
   */
  get(color) {
    if (!color || color === 'inherit') {
      return color;
    }

    let rgb = color; // Hex

    if (/^#/.test(color)) {
      const {
        r,
        g,
        b
      } = this.hexToRgb(color);
      rgb = `${r}, ${g}, ${b}`;
    } else if (/^rgba?/.test(color)) {
      // RGB / RGBA
      const [r, g, b] = color.replace(/[rgba()]/g, '').split(',');
      rgb = `${r}, ${g}, ${b}`;
    } else {
      rgb = `var(--ui-${color})`;
    }

    return rgb;
  },

  /**
   * Check if a color variable is set
   * @param {String} name
   */
  exists(name) {
    const style = window.getComputedStyle(document.body);
    const value = style.getPropertyValue(`--ui-${name}`);
    return value.length > 0;
  }

};

//
var script$e = {
  name: 'ui-button',
  props: {
    active: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'filled'
    },
    type: {
      type: String,
      default: 'button'
    },
    size: {
      type: String,
      default: 'md'
    },
    color: {
      type: String,
      default: 'primary'
    },
    icon: {
      type: [String, Boolean],
      default: false
    },
    animation: {
      type: String,
      default: 'x'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },

  data() {
    return {
      ripple: null
    };
  },

  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-button', `ui-button--${this.theme}`, `ui-button--${this.size}`, {
        'ui-button--active': this.active,
        'ui-button--disabled': this.disabled,
        [`ui-button--${this.color}`]: this.colorExists,
        'ui-button--icon': this.hasIcon,
        'ui-button--text': !!this.$slots.default,
        'ui-button--animate': !!this.$slots.animate,
        'ui-button--loading': this.loading
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-color': color
      };
    },

    hasIcon() {
      return this.icon || !!this.$slots.icon;
    }

  },
  watch: {
    loading: {
      immediate: true,

      handler(active) {
        this.$nextTick(() => {
          if (!this.loader && active) {
            const config = {
              active,
              target: this.$el,
              background: this.color,
              opacity: 0.8,
              color: 'light',
              scale: 0.6
            };
            this.loader = this.$ui.loader(config, this);
          } else if (this.loader) {
            this.loader.active = active;
          }
        });
      }

    }
  },
  methods: {
    showRipple({
      clientX,
      clientY
    }) {
      const {
        width,
        height,
        left,
        top
      } = this.$el.getBoundingClientRect();
      const diameter = Math.max(width, height);
      const radius = diameter / 2;
      const x = clientX - (left + radius);
      const y = clientY - (top + radius);
      this.ripple = {
        width: `${diameter}px`,
        height: `${diameter}px`,
        left: `${x}px`,
        top: `${y}px`
      };
    }

  }
};

function normalizeComponent(template, style, script, scopeId, isFunctionalTemplate, moduleIdentifier /* server only */, shadowMode, createInjector, createInjectorSSR, createInjectorShadow) {
    if (typeof shadowMode !== 'boolean') {
        createInjectorSSR = createInjector;
        createInjector = shadowMode;
        shadowMode = false;
    }
    // Vue.extend constructor export interop.
    const options = typeof script === 'function' ? script.options : script;
    // render functions
    if (template && template.render) {
        options.render = template.render;
        options.staticRenderFns = template.staticRenderFns;
        options._compiled = true;
        // functional template
        if (isFunctionalTemplate) {
            options.functional = true;
        }
    }
    // scopedId
    if (scopeId) {
        options._scopeId = scopeId;
    }
    let hook;
    if (moduleIdentifier) {
        // server build
        hook = function (context) {
            // 2.3 injection
            context =
                context || // cached call
                    (this.$vnode && this.$vnode.ssrContext) || // stateful
                    (this.parent && this.parent.$vnode && this.parent.$vnode.ssrContext); // functional
            // 2.2 with runInNewContext: true
            if (!context && typeof __VUE_SSR_CONTEXT__ !== 'undefined') {
                context = __VUE_SSR_CONTEXT__;
            }
            // inject component styles
            if (style) {
                style.call(this, createInjectorSSR(context));
            }
            // register component module identifier for async chunk inference
            if (context && context._registeredComponents) {
                context._registeredComponents.add(moduleIdentifier);
            }
        };
        // used by ssr in case component is cached and beforeCreate
        // never gets called
        options._ssrRegister = hook;
    }
    else if (style) {
        hook = shadowMode
            ? function (context) {
                style.call(this, createInjectorShadow(context, this.$root.$options.shadowRoot));
            }
            : function (context) {
                style.call(this, createInjector(context));
            };
    }
    if (hook) {
        if (options.functional) {
            // register for functional component in vue file
            const originalRender = options.render;
            options.render = function renderWithStyleInjection(h, context) {
                hook.call(context);
                return originalRender(h, context);
            };
        }
        else {
            // inject component registration as beforeCreate hook
            const existing = options.beforeCreate;
            options.beforeCreate = existing ? [].concat(existing, hook) : [hook];
        }
    }
    return script;
}

const isOldIE = typeof navigator !== 'undefined' &&
    /msie [6-9]\\b/.test(navigator.userAgent.toLowerCase());
function createInjector(context) {
    return (id, style) => addStyle(id, style);
}
let HEAD;
const styles = {};
function addStyle(id, css) {
    const group = isOldIE ? css.media || 'default' : id;
    const style = styles[group] || (styles[group] = { ids: new Set(), styles: [] });
    if (!style.ids.has(id)) {
        style.ids.add(id);
        let code = css.source;
        if (css.map) {
            // https://developer.chrome.com/devtools/docs/javascript-debugging
            // this makes source maps inside style tags work properly in Chrome
            code += '\n/*# sourceURL=' + css.map.sources[0] + ' */';
            // http://stackoverflow.com/a/26603875
            code +=
                '\n/*# sourceMappingURL=data:application/json;base64,' +
                    btoa(unescape(encodeURIComponent(JSON.stringify(css.map)))) +
                    ' */';
        }
        if (!style.element) {
            style.element = document.createElement('style');
            style.element.type = 'text/css';
            if (css.media)
                style.element.setAttribute('media', css.media);
            if (HEAD === undefined) {
                HEAD = document.head || document.getElementsByTagName('head')[0];
            }
            HEAD.appendChild(style.element);
        }
        if ('styleSheet' in style.element) {
            style.styles.push(code);
            style.element.styleSheet.cssText = style.styles
                .filter(Boolean)
                .join('\n');
        }
        else {
            const index = style.ids.size - 1;
            const textNode = document.createTextNode(code);
            const nodes = style.element.childNodes;
            if (nodes[index])
                style.element.removeChild(nodes[index]);
            if (nodes.length)
                style.element.insertBefore(textNode, nodes[index]);
            else
                style.element.appendChild(textNode);
        }
    }
}

/* script */
const __vue_script__$e = script$e;
/* template */

var __vue_render__$e = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('button', _vm._g({
    class: _vm.classes,
    style: _vm.style,
    attrs: {
      "type": _vm.type
    },
    on: {
      "mousedown": _vm.showRipple
    }
  }, _vm.$listeners), [_c('transition', {
    on: {
      "enter": function ($event) {
        _vm.ripple = null;
      }
    }
  }, [_vm.ripple ? _c('div', {
    staticClass: "ui-button__ripple",
    style: _vm.ripple
  }) : _vm._e()]), _vm._v(" "), _vm.$slots.animate ? _c('div', {
    staticClass: "ui-button__animate",
    class: ["ui-button__animate--" + _vm.animation]
  }, [_vm._t("animate")], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "ui-button__content"
  }, [_vm.hasIcon ? _c('div', {
    staticClass: "ui-button__icon"
  }, [_vm._t("icon", function () {
    return [_c('i', {
      class: _vm.icon
    })];
  })], 2) : _vm._e(), _vm._v(" "), _vm.$slots.default ? _c('div', {
    staticClass: "ui-button__text"
  }, [_vm._t("default")], 2) : _vm._e()])], 1);
};

var __vue_staticRenderFns__$e = [];
/* style */

const __vue_inject_styles__$e = function (inject) {
  if (!inject) return;
  inject("data-v-7d3b3423_0", {
    source: ".ui-button{border:0;border-radius:12px;margin:5px;position:relative;overflow:hidden;display:inline-flex;padding:0;align-items:center;justify-content:center;box-sizing:border-box;transition:all .25s ease;user-select:none;color:rgba(var(--ui-text),1);background-color:rgba(var(--ui-color),1)}.ui-button__content{padding:8px 12px;width:100%;display:flex;align-items:center;justify-content:center}.ui-button--disabled{pointer-events:none;opacity:.35}.ui-button--loading{pointer-events:none}.ui-button--icon{display:flex;align-items:center;justify-content:center}.ui-button--icon:not(.ui-button--text) .ui-button__content{padding:8px}.ui-button--icon .ui-button__icon{font-size:1.17em;display:inline-flex}.ui-button--icon .ui-button__icon:not(:only-child){margin-right:.35rem}.ui-button--xl{font-size:1.1rem;border-radius:20px}.ui-button--xl .ui-button__content{padding:15px 20px}.ui-button--xl.ui-button--icon:not(.ui-button--text){font-size:2rem}.ui-button--lg{font-size:1rem;border-radius:15px}.ui-button--lg .ui-button__content{padding:10px 15px}.ui-button--md{font-size:.87rem;border-radius:12px}.ui-button--md .ui-button__content{padding:8px 12px}.ui-button--sm{font-size:.75rem;border-radius:9px}.ui-button--sm .ui-button__content{padding:5px 10px}.ui-button--xs{font-size:.6rem;border-radius:7px}.ui-button--xs .ui-button__content{padding:3px 8px}.ui-button__animate{position:absolute;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;transition:all .25s ease 50ms}.ui-button__animate--x{transform:translate(100%,0)}.ui-button__animate--y{transform:translate(0,100%)}.ui-button__animate--scale{transform:scale(.5);opacity:0}.ui-button__animate--rotate{transform:rotate(-180deg);opacity:0}.ui-button--animate:hover .ui-button__animate--x{transform:translate(0)}.ui-button--animate:hover .ui-button__animate--x+.ui-button__content{transform:translate(-50%,0);opacity:0}.ui-button--animate:hover .ui-button__animate--y{transform:translate(0)}.ui-button--animate:hover .ui-button__animate--y+.ui-button__content{transform:translate(0,-50%);opacity:0}.ui-button--animate:hover .ui-button__animate--scale{transform:scale(1);opacity:1}.ui-button--animate:hover .ui-button__animate--scale+.ui-button__content{transform:scale(1.4);opacity:0}.ui-button--animate:hover .ui-button__animate--rotate{transform:rotate(0);opacity:1}.ui-button--animate:hover .ui-button__animate--rotate+.ui-button__content{transform:rotate(180deg);opacity:0}.ui-button--animate .ui-button__content{transition:all .25s ease 50ms}.ui-button--filled{--ui-text:255,255,255}.ui-button--filled.ui-button--active{box-shadow:0 10px 20px -10px rgba(var(--ui-color),1);transform:translate(0,-3px)}.ui-button--filled:hover{box-shadow:0 10px 20px -10px rgba(var(--ui-color),1);transform:translate(0,-3px)}.ui-button--filled:active{transform:translate(0,-1px)}.ui-button--flat{--ui-text:var(--ui-color);background-color:rgba(var(--ui-color),.15)}.ui-button--flat.ui-button--dark{color:rgba(var(--ui-text),1)}.ui-button--flat:hover{background:rgba(var(--ui-color),.25)}.ui-button--flat:focus{--ui-text:255,255,255;transition:all .25s ease,background .25s ease .25s;background-color:rgba(var(--ui-color),1)}.ui-button--flat.ui-button--active{--ui-text:255,255,255;background-color:rgba(var(--ui-color),1)}.ui-button--border{--ui-text:var(--ui-color);background-color:transparent}.ui-button--border:before{content:\"\";border:2px var(--ui-border-style,solid) rgba(var(--ui-color),1);position:absolute;top:0;left:0;width:100%;height:100%;border-radius:inherit;background:0 0;pointer-events:none;transition:all .25s ease;box-sizing:border-box}.ui-button--border:hover:before{border-color:rgba(var(--ui-color),.5)}.ui-button--border:focus{--ui-text:255,255,255;background-color:rgba(var(--ui-color),1)}.ui-button--border.ui-button--active{--ui-text:255,255,255;background-color:rgba(var(--ui-color),1)}.ui-button--gradient{--ui-text:255,255,255}.ui-button--gradient.ui-button--primary:before,.ui-button--gradient.ui-button--success:before{filter:hue-rotate(45deg)}.ui-button--gradient:before{content:\"\";background-image:linear-gradient(30deg,rgba(var(--ui-color),0) 33%,rgba(var(--ui-color),1) 100%);position:absolute;top:0;left:0;width:100%;height:100%;border-radius:inherit;pointer-events:none;transition:all .4s ease;filter:hue-rotate(-40deg);box-sizing:border-box}.ui-button--gradient .ui-button__content{z-index:0}.ui-button--gradient:hover{transform:translate(0,-3px);box-shadow:0 10px 20px -10px rgba(var(--ui-color),.7)}.ui-button--gradient:hover:before{opacity:0}.ui-button--gradient:active{transform:translate(0,-1px)}.ui-button--gradient.ui-button--active{transform:translate(0,-3px);box-shadow:0 10px 20px -10px rgba(0,0,0,.35)}.ui-button--relief{--ui-text:255,255,255;background:rgba(var(--ui-color),1)}.ui-button--relief .ui-button__content{transition:all .25s ease}.ui-button--relief.ui-button--icon.ui-button--active{height:auto}.ui-button--relief:before{content:\"\";position:absolute;bottom:0;left:0;width:100%;height:calc(100% - 3px);border-radius:inherit;pointer-events:none;transition:all .4s ease;filter:contrast(2) grayscale(.4);border-bottom:3px solid rgba(var(--ui-color),1);box-sizing:border-box}.ui-button--relief:active{transform:translate(0,1px)}.ui-button--relief:active .ui-button__content{padding-bottom:6px}.ui-button--relief:active:before{border-bottom-width:0}.ui-button--relief.ui-button--active{transform:translate(0,1px)}.ui-button--relief.ui-button--active .ui-button__content{padding-bottom:6px}.ui-button--relief.ui-button--active:before{border-bottom:0 solid rgba(var(--ui-color),1)}.ui-button--transparent{--ui-text:var(--ui-color);background-color:transparent}.ui-button--transparent:before{content:\"\";background-color:rgba(var(--ui-color),.1);position:absolute;bottom:0;left:0;width:100%;height:100%;border-radius:inherit;pointer-events:none;transition:all .25s ease;transform:scale(.5);opacity:0;box-sizing:border-box}.ui-button--transparent:active:not(.ui-button--active):before{transform:scale(.9)!important}.ui-button--transparent:hover:before{opacity:1;transform:scale(1)}.ui-button--transparent.ui-button--active:before{background-color:rgba(var(--ui-color),.2);opacity:1;transform:scale(1)}.ui-button--shadow{background-color:rgba(var(--vs-background),1);box-shadow:0 0 0 0 rgba(0,0,0,var(--ui-shadow-opacity))}.ui-button--shadow:active:not(.ui-button--active){transform:translate(0,-1px);box-shadow:0 5px 15px 0 rgba(0,0,0,var(--ui-shadow-opacity))!important}.ui-button--shadow:hover{transform:translate(0,-3px);box-shadow:0 8px 25px 0 rgba(0,0,0,var(--ui-shadow-opacity))}.ui-button--shadow.ui-button--active{transform:translate(0,-3px);box-shadow:0 8px 25px 0 rgba(0,0,0,var(--ui-shadow-opacity))}.ui-button--shadow .ui-loader,.ui-button--transparent .ui-loader{--ui-background:inherit!important;--ui-background-opacity:0.9!important;--ui-color:inherit!important}.ui-button__ripple{--ui-ripple-color:255,255,255;position:absolute;border-radius:50%;transform:scale(0);pointer-events:none;animation:ui-ripple .6s linear;background-image:radial-gradient(circle,rgba(var(--ui-ripple-color),.05),rgba(var(--ui-ripple-color),.6))}.ui-button--flat:not(.ui-button--active) .ui-button__ripple{background-image:none;background-color:rgba(var(--ui-color),.8)}@keyframes ui-ripple{to{transform:scale(4);opacity:0}}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$e = undefined;
/* module identifier */

const __vue_module_identifier__$e = undefined;
/* functional template */

const __vue_is_functional_template__$e = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$e = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$e,
  staticRenderFns: __vue_staticRenderFns__$e
}, __vue_inject_styles__$e, __vue_script__$e, __vue_scope_id__$e, __vue_is_functional_template__$e, __vue_module_identifier__$e, false, createInjector, undefined, undefined);

//
//
//
//
//
//
var script$d = {
  name: 'ui-button-group'
};

/* script */
const __vue_script__$d = script$d;
/* template */

var __vue_render__$d = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "ui-button-group"
  }, [_vm._t("default")], 2);
};

var __vue_staticRenderFns__$d = [];
/* style */

const __vue_inject_styles__$d = function (inject) {
  if (!inject) return;
  inject("data-v-9cbf8d68_0", {
    source: ".ui-button-group{display:flex;align-items:stretch;justify-content:center}.ui-button-group .ui-button{border-radius:0;margin:0;align-items:stretch}.ui-button-group .ui-button:first-child{border-top-left-radius:12px;border-bottom-left-radius:12px}.ui-button-group .ui-button:last-child{border-top-right-radius:12px;border-bottom-right-radius:12px}.ui-button-group .ui-button--border:not(:first-child){margin-left:-2px}.ui-button-group .ui-button--border:not(:first-child):before{border-left:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$d = undefined;
/* module identifier */

const __vue_module_identifier__$d = undefined;
/* functional template */

const __vue_is_functional_template__$d = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$d = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$d,
  staticRenderFns: __vue_staticRenderFns__$d
}, __vue_inject_styles__$d, __vue_script__$d, __vue_scope_id__$d, __vue_is_functional_template__$d, __vue_module_identifier__$d, false, createInjector, undefined, undefined);

//
var script$c = {
  name: 'ui-table',
  props: {
    value: {
      type: [Object, Array]
    },
    reduce: {
      type: Function,
      default: row => row
    },
    data: {
      type: Array,
      required: true
    },
    pk: {
      type: Function,
      default: (row, index) => row.id || index
    },
    columns: {
      type: Array,
      required: true
    },
    striped: {
      type: Boolean,
      default: false
    },
    bordered: {
      type: Boolean,
      default: false
    },
    sticky: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    },
    grid: {
      type: Boolean,
      default: true
    },
    paginate: {
      type: Boolean,
      default: true
    },
    limit: {
      type: Number,
      default: 5
    },
    sort: {
      type: [Boolean, Function],
      default: false
    },
    filter: {
      type: [Boolean, Function],
      default: false
    }
  },

  data() {
    return {
      page: 1,
      sorting: {
        field: null,
        direction: null
      },
      filterValues: {},
      filters: {}
    };
  },

  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-table', {
        'ui-table--grid': this.grid,
        'ui-table--striped': this.striped,
        'ui-table--bordered': this.bordered,
        'ui-table--sticky': this.sticky,
        'ui-table--selectable': this.value !== undefined,
        'ui-table--paginate': this.paginate,
        'ui-table--sortable': this.sort,
        'ui-table--filterable': this.filter,
        [`ui-table--${this.color}`]: this.colorExists
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      const style = {
        '--ui-color': color
      };

      if (this.grid) {
        const widths = [];

        if (this.multiple) {
          widths.push('40px');
        }

        widths.push(...this.columns.map(({
          width
        }) => width || '1fr'));
        style['--ui-grid-columns'] = widths.join(' ');
      }

      return style;
    },

    multiple() {
      return Array.isArray(this.value);
    },

    selected: {
      get() {
        if (!this.value) {
          return [];
        }

        if (!this.multiple) {
          return [this.value];
        }

        return this.value;
      },

      set(values) {
        this.$emit('input', values);
      }

    },
    selectedAll: {
      get() {
        return this.selected.length === this.data.length;
      },

      set(value) {
        if (value) {
          this.selected = this.data.slice().map(row => this.reduce(row));
        } else {
          this.selected = [];
        }
      }

    },

    rows() {
      let data = this.data.slice();

      if (this.filter === true) {
        const {
          filters
        } = this;
        Object.entries(filters).forEach(([field, value]) => {
          if (value.length === 0) {
            return;
          }

          const column = this.columns.find(({
            field: key
          }) => key === field);
          const {
            filter = ({
              row
            }) => String(row[field]).indexOf(value) !== -1
          } = column;
          data = data.filter(row => filter({
            row,
            field,
            value,
            filters
          }));
        });
      }

      if (this.paginate) {
        const offset = (this.page - 1) * this.limit;
        data = data.slice(offset, offset + this.limit);
      }

      if (this.sort === true) {
        const {
          field,
          direction
        } = this.sorting;

        if (field) {
          const column = this.columns.find(({
            field: key
          }) => key === field);
          const {
            sort = ({
              [field]: a
            }, {
              [field]: b
            }) => {
              const type = typeof a;
              const modifier = direction === 'desc' ? -1 : 1;

              if (type === 'number') {
                return (a - b) * modifier;
              }

              if (type === 'array') {
                throw new Error(`ui-table: Please provide a sort function for the ${field} column as it contain arrays.`);
              } else {
                return a.localeCompare(b) * modifier;
              }
            }
          } = column;
          data = data.sort((a, b) => sort(a, b, direction));
        }
      }

      return data;
    },

    pages() {
      return Math.ceil(this.data.length / this.limit);
    }

  },
  methods: {
    select(row) {
      if (this.value === undefined) {
        return;
      }

      if (this.multiple) {
        if (row === 'all') {
          this.selectedAll = !this.selectedAll;
        } else {
          const index = this.selected.indexOf(row);

          if (index !== -1) {
            this.selected.splice(index, 1);
          } else {
            this.selected.push(row);
          }
        }
      } else if (this.value === row) {
        this.selected = null;
      } else {
        this.selected = row;
      }
    },

    isSelected(row) {
      if (this.value === undefined) {
        return false;
      }

      if (row === 'all') {
        return this.selectedAll;
      }

      return this.selected.includes(row);
    },

    // eslint-disable-next-line func-names
    sortColumn: debounce(function (column) {
      if (!this.sort) {
        return;
      }

      const {
        field,
        direction
      } = this.sorting;

      if (field !== column.field) {
        this.sorting = {
          field: column.field,
          direction: 'asc'
        };
      } else if (direction === 'asc') {
        this.$set(this.sorting, 'direction', 'desc');
      } else {
        this.sorting = {
          field: null,
          direction: null
        };
      }

      if (typeof this.sort === 'function') {
        this.sort(column, direction);
      }
    }, 200),
    // eslint-disable-next-line func-names
    filterColumn: debounce(function (column, value) {
      if (value === null || (value === null || value === void 0 ? void 0 : value.length) === 0) {
        this.$delete(this.filters, column.field);
      } else {
        this.$set(this.filters, column.field, value);
      }

      if (typeof this.filter === 'function') {
        this.filter(this.filters);
      }
    }, 200)
  }
};

/* script */
const __vue_script__$c = script$c;
/* template */

var __vue_render__$c = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.classes,
    style: _vm.style
  }, [_c('table', {
    staticClass: "ui-table__content"
  }, [_c('thead', {
    staticClass: "ui-table__headers"
  }, [_c('tr', {
    staticClass: "ui-table__row"
  }, [_vm.multiple ? _c('th', {
    staticClass: "ui-table__header ui-table__checkbox"
  }, [_c('ui-checkbox', {
    attrs: {
      "indeterminate": !_vm.selectedAll && _vm.selected.length > 0
    },
    model: {
      value: _vm.selectedAll,
      callback: function ($$v) {
        _vm.selectedAll = $$v;
      },
      expression: "selectedAll"
    }
  })], 1) : _vm._e(), _vm._v(" "), _vm._l(_vm.columns, function (column) {
    var _obj;

    return _c('th', {
      key: column.field,
      staticClass: "ui-table__header",
      attrs: {
        "data-field": column.field
      },
      on: {
        "click": function ($event) {
          return _vm.sortColumn(column);
        }
      }
    }, [_vm._t("header:" + column.field, function () {
      return [_vm._v("\n            " + _vm._s(column.name) + "\n          ")];
    }, null, column), _vm._v(" "), _vm.sort ? [_c('i', {
      staticClass: "ui-table__sort",
      class: (_obj = {}, _obj["ui-table__sort--" + _vm.sorting.direction] = _vm.sorting.field === column.field, _obj)
    })] : _vm._e()], 2);
  })], 2), _vm._v(" "), _vm.filter ? _c('tr', {
    staticClass: "ui-table__row ui-table__filters"
  }, [_vm.multiple ? _c('th') : _vm._e(), _vm._v(" "), _vm._l(_vm.columns, function (column) {
    return _c('th', {
      key: column.field,
      staticClass: "ui-table__header ui-table__filter"
    }, [column.filter !== false ? _vm._t("filter:" + column.field, function () {
      return [_c('ui-input', {
        attrs: {
          "theme": "border",
          "color": _vm.color,
          "autocomplete": "off"
        },
        on: {
          "input": function ($event) {
            return _vm.filterColumn(column, $event);
          }
        },
        model: {
          value: _vm.filterValues[column.field],
          callback: function ($$v) {
            _vm.$set(_vm.filterValues, column.field, $$v);
          },
          expression: "filterValues[column.field]"
        }
      })];
    }, {
      "apply": _vm.filterColumn
    }, column) : _vm._e()], 2);
  })], 2) : _vm._e()]), _vm._v(" "), _c('tbody', _vm._l(_vm.rows, function (row, index) {
    return _c('tr', {
      key: _vm.pk(row, index),
      staticClass: "ui-table__row",
      class: {
        'ui-table__row--selected': _vm.isSelected(_vm.reduce(row))
      },
      on: {
        "click": function ($event) {
          _vm.select(_vm.reduce(row));
        }
      }
    }, [_vm.multiple ? _c('td', {
      staticClass: "ui-table__cell ui-table__checkbox"
    }, [_c('ui-checkbox', {
      attrs: {
        "data": _vm.reduce(row)
      },
      model: {
        value: _vm.selected,
        callback: function ($$v) {
          _vm.selected = $$v;
        },
        expression: "selected"
      }
    })], 1) : _vm._e(), _vm._v(" "), _vm._l(_vm.columns, function (column) {
      return _c('td', {
        key: column.field,
        staticClass: "ui-table__cell"
      }, [_vm._t(column.field, function () {
        return [_vm._v("\n            " + _vm._s(row[column.field]) + "\n          ")];
      }, null, row)], 2);
    })], 2);
  }), 0)]), _vm._v(" "), _vm.pages > 1 ? _c('ui-pagination', {
    attrs: {
      "length": _vm.pages
    },
    model: {
      value: _vm.page,
      callback: function ($$v) {
        _vm.page = $$v;
      },
      expression: "page"
    }
  }) : _vm._e()], 1);
};

var __vue_staticRenderFns__$c = [];
/* style */

const __vue_inject_styles__$c = function (inject) {
  if (!inject) return;
  inject("data-v-00eb5902_0", {
    source: ".ui-table{width:100%;display:flex;flex-direction:column}.ui-table__container{overflow:hidden}.ui-table__content{display:table;width:100%;min-width:500px;font-size:.9rem;margin:0;border:0;border-collapse:collapse;overflow:auto}.ui-table tfoot,.ui-table thead{border:0;width:100%}.ui-table__headers{width:100%;background-color:rgba(var(--ui-background),1)}.ui-table__headers .ui-table__header{border-bottom:1px solid rgba(var(--ui-grey-4),1)}.ui-table__header{border:0;text-align:left;font-size:.9rem;font-weight:600}.ui-table__row{border:0;background-color:inherit}.ui-table__row:hover .ui-table__cell{background-color:rgba(var(--ui-grey-1),1)}.ui-table__cell{border:0;padding:10px 12px;transition:all .25s ease;font-size:.8rem}.ui-table__checkbox{padding:5px 5px 0;text-align:center}.ui-table--selectable tbody .ui-table__row{cursor:pointer}.ui-table--selectable tbody .ui-table__row--selected .ui-table__cell{background-color:rgba(var(--ui-color),.1);color:rgba(var(--ui-color),1)}.ui-table--bordered .ui-table__header{border-bottom:1px solid rgba(var(--ui-grey-4),1)}.ui-table--bordered .ui-table__cell{border-bottom:1px solid rgba(var(--ui-grey-3),1)}.ui-table--bordered .ui-table__row:last-child .ui-table__cell{border-bottom:0}.ui-table--striped .ui-table__row:nth-child(2n) .ui-table__cell{background-color:rgba(var(--ui-grey-1),1)}.ui-table--striped .ui-table__row:nth-child(2n) .ui-table__cell:first-child{border-top-left-radius:8px;border-bottom-left-radius:8px}.ui-table--striped .ui-table__row:nth-child(2n) .ui-table__cell:last-child{border-top-right-radius:8px;border-bottom-right-radius:8px}.ui-table--striped .ui-table__row:nth-child(2n):hover .ui-table__cell{background-color:rgba(var(--ui-grey-2),1)}.ui-table--sticky tfoot,.ui-table--sticky thead{position:sticky}.ui-table--sticky thead{inset-block-start:0}.ui-table--sticky tfoot{inset-block-end:0}.ui-table--grid .ui-table__content{display:grid;grid-template-columns:var(--ui-grid-columns)}.ui-table--grid tbody,.ui-table--grid thead,.ui-table--grid tr{display:contents}.ui-table--grid.ui-table--sticky thead{position:relative}.ui-table--grid.ui-table--sticky thead th{position:sticky;top:0}.ui-table--paginate .ui-pagination{align-self:flex-end;margin:.5rem 0}.ui-table--sortable .ui-table__header:not(.ui-table__filter){transition:background-color .2s ease-in;cursor:pointer}.ui-table--sortable .ui-table__header:not(.ui-table__filter):hover{background-color:rgba(var(--ui-grey-2),1)}.ui-table--sortable .ui-table__header:not(.ui-table__filter):hover .ui-table__sort:after,.ui-table--sortable .ui-table__header:not(.ui-table__filter):hover .ui-table__sort:before{opacity:.5}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort{position:relative}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort:after,.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort:before{content:\"\";position:absolute;width:4px;height:4px;display:inline-block;vertical-align:middle;pointer-events:none;transform-origin:center;transition:all .25s ease;transform:translate(80%,80%) rotate(45deg);opacity:0}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort:before{top:0;border-top:1px solid rgba(var(--ui-text),1);border-left:1px solid rgba(var(--ui-text),1);transform:translate(80%,80%) rotate(45deg)}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort:after{top:4px;border-bottom:1px solid rgba(var(--ui-text),1);border-right:1px solid rgba(var(--ui-text),1)}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort.ui-table__sort--asc:before,.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort.ui-table__sort--desc:before{opacity:0}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort.ui-table__sort--asc:after,.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort.ui-table__sort--desc:after{top:2px;opacity:.75}.ui-table--sortable .ui-table__header:not(.ui-table__filter) .ui-table__sort.ui-table__sort--desc:after{transform:translate(80%,100%) rotate(-135deg)}.ui-table--filterable .ui-table__filter{padding-top:5px;padding-bottom:5px}.ui-table--filterable .ui-table__filter .ui-input{width:100%}.ui-table--filterable .ui-table__filter .ui-input input{padding-left:0}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$c = undefined;
/* module identifier */

const __vue_module_identifier__$c = undefined;
/* functional template */

const __vue_is_functional_template__$c = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$c = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$c,
  staticRenderFns: __vue_staticRenderFns__$c
}, __vue_inject_styles__$c, __vue_script__$c, __vue_scope_id__$c, __vue_is_functional_template__$c, __vue_module_identifier__$c, false, createInjector, undefined, undefined);

//
var script$b = {
  name: 'ui-checkbox',
  props: {
    value: {
      default: undefined
    },
    checked: {
      type: Boolean,
      default: false
    },
    indeterminate: {
      type: Boolean,
      default: false
    },
    data: {
      default: true
    },
    trueValue: {
      default: undefined
    },
    falseValue: {
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    },
    size: {
      type: String,
      default: 'md'
    },
    reverse: {
      type: Boolean,
      default: false
    },
    lineThrough: {
      type: Boolean,
      default: false
    },
    icon: {
      type: String,
      default: null
    }
  },
  computed: {
    current: {
      get() {
        if (this.value === undefined) {
          return this.checked;
        }

        if (this.indeterminate) {
          return this.indeterminate;
        }

        return this.value;
      },

      set(value) {
        this.$emit('input', value);
      }

    },

    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-checkbox', `ui-checkbox--${this.size}`, {
        'ui-checkbox--disabled': this.disabled,
        'ui-checkbox--reverse': this.reverse,
        'ui-checkbox--line-through': this.lineThrough,
        [`ui-checkbox--${this.color}`]: this.colorExists
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-color': color
      };
    },

    hasIcon() {
      return this.icon || !!this.$slots.icon;
    }

  }
};

/* script */
const __vue_script__$b = script$b;
/* template */

var __vue_render__$b = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('label', {
    class: _vm.classes,
    style: _vm.style
  }, [_c('input', {
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.current,
      expression: "current"
    }],
    attrs: {
      "type": "checkbox",
      "true-value": _vm.trueValue || _vm.data,
      "false-value": _vm.falseValue,
      "indeterminate": _vm.indeterminate
    },
    domProps: {
      "value": _vm.data,
      "checked": _vm.checked,
      "checked": Array.isArray(_vm.current) ? _vm._i(_vm.current, _vm.data) > -1 : _vm._q(_vm.current, _vm.trueValue || _vm.data)
    },
    on: {
      "change": function ($event) {
        var $$a = _vm.current,
            $$el = $event.target,
            $$c = $$el.checked ? _vm.trueValue || _vm.data : _vm.falseValue;

        if (Array.isArray($$a)) {
          var $$v = _vm.data,
              $$i = _vm._i($$a, $$v);

          if ($$el.checked) {
            $$i < 0 && (_vm.current = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.current = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.current = $$c;
        }
      }
    }
  }), _vm._v(" "), _c('div', {
    staticClass: "ui-checkbox__mask"
  }, [_vm._t("icon", function () {
    var _obj;

    return [_c('i', {
      class: (_obj = {}, _obj[_vm.icon] = _vm.hasIcon, _obj['ui-checkbox__tick'] = !_vm.indeterminate && !_vm.hasIcon, _obj['ui-checkbox__line'] = _vm.indeterminate && !_vm.hasIcon, _obj)
    })];
  })], 2), _vm._v(" "), _c('div', {
    staticClass: "ui-checkbox__content"
  }, [_vm._t("default")], 2)]);
};

var __vue_staticRenderFns__$b = [];
/* style */

const __vue_inject_styles__$b = function (inject) {
  if (!inject) return;
  inject("data-v-34459d40_0", {
    source: ".ui-checkbox{--ui-tick-color:255,255,255;cursor:pointer;display:inline-flex;flex-direction:row;align-items:center;position:relative}.ui-checkbox__content{display:flex;align-items:center;justify-content:center;padding:0 7px;position:relative;user-select:none;cursor:pointer;font-size:.9rem;transition:all .25s ease}.ui-checkbox__content:empty{padding:0}.ui-checkbox__content:before{content:\"\";position:absolute;width:0;height:2px;background-color:rgba(var(--ui-text),.6);transition:width .25s ease}.ui-checkbox--disabled{opacity:.5;pointer-events:none}.ui-checkbox--reverse{flex-direction:row-reverse}.ui-checkbox--line-through input:checked~.ui-checkbox__content{opacity:.4}.ui-checkbox--line-through input:checked~.ui-checkbox__content:before{width:calc(100% - 10px)}.ui-checkbox+.ui-checkbox{margin-left:10px}.ui-checkbox input{cursor:pointer;appearance:none;margin:0;position:absolute;top:0;left:0;width:var(--ui-checkbox-size);height:var(--ui-checkbox-size);z-index:1}.ui-checkbox:hover .ui-checkbox__mask{background-color:rgba(var(--ui-grey-3),1)}.ui-checkbox:active .ui-checkbox__mask{background-color:rgba(var(--ui-grey-4),1)}.ui-checkbox__mask{width:var(--ui-checkbox-size);height:var(--ui-checkbox-size);cursor:pointer;position:relative;border:2px solid rgba(var(--ui-grey-4),1);border-radius:30%;box-sizing:border-box;transition:all .25s ease;background-color:transparent}.ui-checkbox__mask:before{content:\"\";position:absolute;top:0;left:0;width:100%;height:100%;background-color:rgba(var(--ui-color),1);transform:scale(.3);border-radius:inherit;opacity:0;transition:all .25s ease}.ui-checkbox__mask i{position:absolute;top:0;left:0;opacity:0;transform:scale(0);transition:opacity .25s ease .15s,transform .25s ease;font-size:1.1em}.ui-checkbox__mask i:not(.ui-checkbox__tick){color:rgba(var(--ui-tick-color),1)}.ui-checkbox__mask i:not(.ui-checkbox__tick) svg{fill:rgba(var(--ui-tick-color),1)!important;width:19px;height:19px}.ui-checkbox__mask .ui-checkbox__line{position:absolute;top:calc(50% - 1px);left:25%;width:50%;height:2px;background-color:rgba(var(--ui-tick-color),1);border-radius:5px}.ui-checkbox__mask .ui-checkbox__tick{position:absolute;top:10%;left:11%;right:0;width:70%;height:50%;transform:rotateZ(-45deg)}.ui-checkbox__mask .ui-checkbox__tick:after,.ui-checkbox__mask .ui-checkbox__tick:before{content:\"\";position:absolute;background-color:rgba(var(--ui-tick-color),1);border-radius:5px;opacity:0}.ui-checkbox__mask .ui-checkbox__tick:before{left:0;bottom:0;width:2px;height:70%;transform:scaleY(0);transform-origin:top;transition:.25s ease transform .1s,.2s ease opacity}.ui-checkbox__mask .ui-checkbox__tick:after{left:0;bottom:0;width:95%;height:2.5px;transform:scaleX(0);transform-origin:left;transition:.2s ease transform .3s,.2s ease opacity}.ui-checkbox input:checked+.ui-checkbox__mask:before{opacity:1;transform:scale(1.2)}.ui-checkbox input:checked+.ui-checkbox__mask i{opacity:1}.ui-checkbox input:checked+.ui-checkbox__mask i:not(.ui-checkbox__tick){transform:scale(1)}.ui-checkbox input:checked+.ui-checkbox__mask .ui-checkbox__tick:before{opacity:1;transform:scaleY(1)}.ui-checkbox input:checked+.ui-checkbox__mask .ui-checkbox__tick:after{opacity:1;transform:scaleX(1)}.ui-checkbox input:checked:hover+.ui-checkbox__mask{box-shadow:0 3px 15px 0 rgba(var(--ui-color),.35)}.ui-checkbox--xs{--ui-checkbox-size:15px}.ui-checkbox--xs input:checked~.ui-checkbox__mask:before{transform:scale(1.28)}.ui-checkbox--xs .ui-checkbox__tick{width:80%;height:60%;transform:translate(-12%,-13%) rotateZ(-45deg)}.ui-checkbox--xs .ui-checkbox__tick:before{width:2px}.ui-checkbox--xs .ui-checkbox__tick:after{height:2px}.ui-checkbox--sm{--ui-checkbox-size:19px}.ui-checkbox--sm input:checked~.ui-checkbox__mask:before{transform:scale(1.21)}.ui-checkbox--md{--ui-checkbox-size:23px}.ui-checkbox--lg{--ui-checkbox-size:28px}.ui-checkbox--lg .ui-checkbox__tick:before{width:2.5px}.ui-checkbox--lg .ui-checkbox__tick:after{height:3px}.ui-checkbox--xl{--ui-checkbox-size:32px}.ui-checkbox--xl .ui-checkbox__tick:before{width:3.5px}.ui-checkbox--xl .ui-checkbox__tick:after{height:3.9px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$b = undefined;
/* module identifier */

const __vue_module_identifier__$b = undefined;
/* functional template */

const __vue_is_functional_template__$b = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$b = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$b,
  staticRenderFns: __vue_staticRenderFns__$b
}, __vue_inject_styles__$b, __vue_script__$b, __vue_scope_id__$b, __vue_is_functional_template__$b, __vue_module_identifier__$b, false, createInjector, undefined, undefined);

//
var script$a = {
  name: 'ui-tippy',
  props: {
    value: {
      required: true
    },
    config: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    instance: {
      get() {
        return this.value;
      },

      set(instance) {
        this.$emit('input', instance);
      }

    }
  },

  mounted() {
    const {
      content
    } = this.$refs;
    this.instance = tippy(this.$el, {
      content,
      ...this.config
    });
  },

  beforeDestroy() {
    this.instance.destroy();
  }

};

/* script */
const __vue_script__$a = script$a;
/* template */

var __vue_render__$a = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "ui-tippy"
  }, [_vm._t("default"), _vm._v(" "), _c('div', {
    ref: "content",
    staticClass: "ui-tippy__content"
  }, [_vm._t("content")], 2)], 2);
};

var __vue_staticRenderFns__$a = [];
/* style */

const __vue_inject_styles__$a = function (inject) {
  if (!inject) return;
  inject("data-v-320cff1d_0", {
    source: ".ui-tippy{display:inline-flex}.ui-tippy>.ui-button{margin-top:0}.tippy-box[data-placement^=top]>.tippy-svg-arrow svg{top:16px}.tippy-box[data-placement^=left]>.tippy-svg-arrow{right:1px}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$a = undefined;
/* module identifier */

const __vue_module_identifier__$a = undefined;
/* functional template */

const __vue_is_functional_template__$a = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$a = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$a,
  staticRenderFns: __vue_staticRenderFns__$a
}, __vue_inject_styles__$a, __vue_script__$a, __vue_scope_id__$a, __vue_is_functional_template__$a, __vue_module_identifier__$a, false, createInjector, undefined, undefined);

//
var script$9 = {
  name: 'ui-tooltip',
  props: {
    value: {
      type: Boolean
    },
    text: {
      type: String
    },
    theme: {
      type: String,
      default: 'default'
    },
    trigger: {
      type: String,
      default: 'mouseenter'
    },
    color: {
      type: String,
      default: 'dark'
    },
    placement: {
      type: String,
      default: 'top'
    },
    arrow: {
      type: [Boolean, String, Element],
      default: roundArrow
    },
    animation: {
      type: [String, Boolean],
      default: 'scale'
    },
    duration: {
      type: [Number, Array],
      default: 200
    },
    delay: {
      type: [Number, Array],
      default: 0
    },
    interactive: {
      type: Boolean,
      default: false
    },
    tippy: {
      type: Object,
      default: () => ({})
    }
  },

  data() {
    return {
      instance: null
    };
  },

  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-tooltip', {
        [`ui-tooltip--${this.color}`]: this.colorExists
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-color': color
      };
    },

    config() {
      return { ...this.tippy,
        arrow: this.arrow,
        theme: this.theme,
        trigger: this.trigger,
        placement: this.placement,
        animation: this.animation,
        duration: this.duration,
        delay: this.delay,
        interactive: this.interactive,
        onCreate: instance => {
          const classes = ['ui-tooltip'];
          const colorExists = colors.exists(this.color, true);

          if (colorExists) {
            classes.push(`ui-tooltip--${this.color}`);
          } else {
            instance.popper.style.setProperty('--ui-color', colors.get(this.color));
          }

          instance.popper.classList.add(...classes);

          if (this.value) {
            instance.show();
          }
        }
      };
    }

  },
  watch: {
    value(visible) {
      if (visible) {
        this.show();
      } else {
        this.hide();
      }
    },

    config(data) {
      this.instance.setProps(data);
    },

    color(value) {
      this.instance.popper.style.setProperty('--ui-color', colors.get(value));
    }

  },
  methods: {
    show() {
      this.instance.show();
    },

    hide() {
      this.instance.hide();
    }

  }
};

/* script */
const __vue_script__$9 = script$9;
/* template */

var __vue_render__$9 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('ui-tippy', {
    attrs: {
      "config": _vm.config
    },
    scopedSlots: _vm._u([{
      key: "content",
      fn: function () {
        return [_vm._t("tooltip", function () {
          return [_vm._v("\n      " + _vm._s(_vm.text) + "\n    ")];
        })];
      },
      proxy: true
    }], null, true),
    model: {
      value: _vm.instance,
      callback: function ($$v) {
        _vm.instance = $$v;
      },
      expression: "instance"
    }
  }, [_vm._t("default")], 2);
};

var __vue_staticRenderFns__$9 = [];
/* style */

const __vue_inject_styles__$9 = function (inject) {
  if (!inject) return;
  inject("data-v-b3ba00cc_0", {
    source: ".ui-tooltip .tippy-content{padding:5px 9px}.ui-tooltip .tippy-box{font-size:14px;line-height:1.4;border-radius:5px}.ui-tooltip .tippy-box[data-theme=default]{--ui-text:255,255,255;background-color:rgba(var(--ui-color),1);color:rgba(var(--ui-text),1)}.ui-tooltip .tippy-box[data-theme=default] .tippy-arrow{color:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=default] .tippy-svg-arrow{fill:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=border]{background-color:rgba(var(--ui-background),1);border:1px solid rgba(var(--ui-color),1);color:rgba(var(--ui-text),1)}.ui-tooltip .tippy-box[data-theme=border] .tippy-arrow{color:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=border] .tippy-svg-arrow svg:first-child{fill:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=border] .tippy-svg-arrow svg:last-child{margin-top:-1px;fill:rgba(var(--ui-background),1)}.ui-tooltip .tippy-box[data-theme=border-bottom]{background-color:rgba(var(--ui-background),1);border-bottom:2px solid rgba(var(--ui-color),1);color:rgba(var(--ui-text),1)}.ui-tooltip .tippy-box[data-theme=border-bottom] .tippy-arrow{color:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=border-bottom] .tippy-svg-arrow svg{margin-top:1px;fill:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=shadow]{background-color:rgba(var(--ui-background),1);box-shadow:2px 2px 10px 0 rgba(0,0,0,var(--ui-shadow-opacity));color:rgba(var(--ui-text),1)}.ui-tooltip .tippy-box[data-theme=shadow] .tippy-arrow{color:rgba(var(--ui-color),1)}.ui-tooltip .tippy-box[data-theme=shadow] .tippy-svg-arrow{z-index:1;fill:rgba(var(--ui-background),1)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$9 = undefined;
/* module identifier */

const __vue_module_identifier__$9 = undefined;
/* functional template */

const __vue_is_functional_template__$9 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$9 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$9,
  staticRenderFns: __vue_staticRenderFns__$9
}, __vue_inject_styles__$9, __vue_script__$9, __vue_scope_id__$9, __vue_is_functional_template__$9, __vue_module_identifier__$9, false, createInjector, undefined, undefined);

//
var script$8 = {
  name: 'ui-input',
  props: {
    value: {
      default: ''
    },
    type: {
      type: String,
      default: 'text'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    theme: {
      type: String,
      default: 'default'
    },
    color: {
      type: String,
      default: 'primary'
    },
    label: {
      type: String
    },
    placeholder: {
      type: String
    },
    labelPlaceholder: {
      type: String
    },
    progress: {
      type: Number
    },
    message: {
      type: String
    },
    icon: {
      type: String
    },
    iconRight: {
      type: String
    },
    success: {
      type: Boolean
    },
    warn: {
      type: Boolean
    },
    error: {
      type: Boolean
    }
  },
  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-input', `ui-input--${this.theme}`, `ui-input--${this.size}`, {
        'ui-input--empty': !this.value,
        'ui-input--not-empty': !!this.value,
        'ui-input--disabled': this.disabled,
        'ui-input--icon': this.hasIcon,
        'ui-input--icon-right': this.hasIconRight,
        'ui-input--state': this.state,
        [`ui-input--${this.currentColor}`]: this.currentColor
      }];
    },

    style() {
      let color = null;

      if (!this.state && !this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-color': color
      };
    },

    labelText() {
      return this.label || this.labelPlaceholder || this.$slots.label;
    },

    hasIcon() {
      return this.icon || !!this.$slots.icon;
    },

    hasIconRight() {
      return this.iconRight || !!this.$slots['icon-right'];
    },

    state() {
      if (this.success) {
        return 'success';
      }

      if (this.warn) {
        return 'warn';
      }

      if (this.error) {
        return 'danger';
      }

      return false;
    },

    currentColor() {
      if (this.state) {
        return this.state;
      }

      if (this.colorExists) {
        return this.color;
      }

      return null;
    },

    inputValue: {
      get() {
        return this.value;
      },

      set(value) {
        this.$emit('input', value);
      }

    }
  }
};

/* script */
const __vue_script__$8 = script$8;
/* template */

var __vue_render__$8 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.classes,
    style: _vm.style
  }, [_c('label', {
    staticClass: "ui-input__content"
  }, [_vm.type === 'checkbox' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputValue,
      expression: "inputValue"
    }],
    attrs: {
      "placeholder": _vm.placeholder,
      "disabled": _vm.disabled,
      "type": "checkbox"
    },
    domProps: {
      "checked": Array.isArray(_vm.inputValue) ? _vm._i(_vm.inputValue, null) > -1 : _vm.inputValue
    },
    on: {
      "change": function ($event) {
        var $$a = _vm.inputValue,
            $$el = $event.target,
            $$c = $$el.checked ? true : false;

        if (Array.isArray($$a)) {
          var $$v = null,
              $$i = _vm._i($$a, $$v);

          if ($$el.checked) {
            $$i < 0 && (_vm.inputValue = $$a.concat([$$v]));
          } else {
            $$i > -1 && (_vm.inputValue = $$a.slice(0, $$i).concat($$a.slice($$i + 1)));
          }
        } else {
          _vm.inputValue = $$c;
        }
      }
    }
  }, 'input', _vm.$attrs, false)) : _vm.type === 'radio' ? _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputValue,
      expression: "inputValue"
    }],
    attrs: {
      "placeholder": _vm.placeholder,
      "disabled": _vm.disabled,
      "type": "radio"
    },
    domProps: {
      "checked": _vm._q(_vm.inputValue, null)
    },
    on: {
      "change": function ($event) {
        _vm.inputValue = null;
      }
    }
  }, 'input', _vm.$attrs, false)) : _c('input', _vm._b({
    directives: [{
      name: "model",
      rawName: "v-model",
      value: _vm.inputValue,
      expression: "inputValue"
    }],
    attrs: {
      "placeholder": _vm.placeholder,
      "disabled": _vm.disabled,
      "type": _vm.type
    },
    domProps: {
      "value": _vm.inputValue
    },
    on: {
      "input": function ($event) {
        if ($event.target.composing) {
          return;
        }

        _vm.inputValue = $event.target.value;
      }
    }
  }, 'input', _vm.$attrs, false)), _vm._v(" "), _vm.labelText ? _c('div', {
    class: {
      'ui-input__label': _vm.labelText,
      'ui-input__label--placeholder': _vm.labelPlaceholder
    }
  }, [_vm._t("label", function () {
    return [_vm._v("\n        " + _vm._s(_vm.labelText) + "\n      ")];
  }, {
    "text": _vm.labelText
  })], 2) : _vm._e(), _vm._v(" "), _vm.theme === 'border' ? _c('div', {
    staticClass: "ui-input__border"
  }) : _vm._e(), _vm._v(" "), _vm.hasIcon ? _c('div', {
    staticClass: "ui-input__icon"
  }, [_vm._t("icon", function () {
    return [_c('i', {
      class: _vm.icon
    })];
  })], 2) : _vm._e(), _vm._v(" "), _vm.hasIconRight ? _c('div', {
    staticClass: "ui-input__icon ui-input__icon--right"
  }, [_vm._t("icon-right", function () {
    return [_c('i', {
      class: _vm.iconRight
    })];
  })], 2) : _vm._e()]), _vm._v(" "), _vm.progress >= 0 ? _c('div', {
    staticClass: "ui-progress"
  }, [_c('div', {
    staticClass: "ui-progress__bar",
    class: {
      'bg--danger': _vm.progress <= 0.25,
      'bg--warn': _vm.progress > 0.25 && _vm.progress <= 0.75,
      'bg--success': _vm.progress > 0.75 || _vm.progress >= 1
    },
    style: {
      width: _vm.progress * 100 + "%"
    }
  })]) : _vm._e(), _vm._v(" "), _c('transition', {
    on: {
      "before-enter": function (el) {
        return el.style.maxHeight = 0;
      },
      "enter": function (el, done) {
        return el.style.maxHeight = el.scrollHeight + "px", done();
      },
      "leave": function (el) {
        return el.style.maxHeight = 0;
      }
    }
  }, [_vm.message || _vm.$slots.message ? _c('div', {
    staticClass: "ui-input__message"
  }, [_vm._t("message", function () {
    return [_vm._v("\n        " + _vm._s(_vm.message) + "\n      ")];
  })], 2) : _vm._e()])], 1);
};

var __vue_staticRenderFns__$8 = [];
/* style */

const __vue_inject_styles__$8 = function (inject) {
  if (!inject) return;
  inject("data-v-941458de_0", {
    source: ".ui-input{position:relative;display:inline-flex;align-self:flex-end;justify-content:center;flex-direction:column;color:rgba(var(--ui-text),1);width:200px}.ui-input input{appearance:none;padding:7px 13px 7px 10px;border:2px solid transparent;border-radius:inherit;transition:all .25s ease;width:100%;box-sizing:border-box;color:inherit}.ui-input input::placeholder{color:rgba(var(--ui-text),.4);transition:all .25s ease;font-size:.7rem}.ui-input input:focus{padding-left:15px}.ui-input input:focus::placeholder{opacity:0;padding-left:15px}.ui-input__content{display:flex;flex-direction:column-reverse;justify-content:flex-start;align-items:flex-start;position:relative;border-radius:inherit;width:100%}.ui-input__label{display:inline-flex;align-items:center;margin:5px 13px;font-size:.75rem;cursor:pointer;user-select:none;pointer-events:auto;transition:all .25s ease}input[required]~.ui-input__label{font-weight:600}.ui-input__label--placeholder{min-height:.95rem;font-size:.7rem;pointer-events:none;transform:translate(-1px,30px);color:rgba(var(--ui-text),.4)}.ui-input--not-empty .ui-input__label--placeholder,input:focus~.ui-input__label--placeholder{font-size:.8rem;pointer-events:auto;color:rgba(var(--ui-text),1);transform:translate(0,0)}.ui-input__message{margin:5px 10px 0;font-size:.7rem;transition:all .25s ease;overflow:hidden}.ui-input--disabled{opacity:.6;pointer-events:none}.ui-input--default{border-radius:12px}.ui-input--default input{background-color:rgba(var(--ui-grey-2),1)}.ui-input--default input:focus{background-color:rgba(var(--ui-grey-3),1)}.ui-input--transparent{--ui-color:transparent}.ui-input--filled{--ui-text:rgba(var(--ui-color), 1);border-radius:12px}.ui-input--filled input{background-color:rgba(var(--ui-color),.1);color:rgba(var(--ui-color),1)}.ui-input--filled input::placeholder{color:rgba(var(--ui-color),1)}.ui-input--filled i{color:rgba(var(--ui-color),1)}.ui-input--filled i svg{fill:rgba(var(--ui-color),1)}.ui-input--border{border-radius:0}.ui-input--border input{background-color:transparent;border-radius:0}.ui-input--border input:focus~.ui-input__border:after{width:100%}.ui-input--border input:focus~.ui-input__icon,.ui-input--border input:focus~.ui-input__label{color:rgba(var(--ui-color),1)}.ui-input--border .ui-input__border{position:absolute;left:0;bottom:0;width:100%;height:2px;border-radius:5px;background-color:rgba(var(--ui-grey-3),1);pointer-events:none}.ui-input--border .ui-input__border:after{content:\"\";position:absolute;left:50%;bottom:0;width:0;height:2px;background-color:rgba(var(--ui-color),1);border-radius:inherit;pointer-events:none;transform:translateX(-50%);transition:all .25s ease}.ui-input--shadow{border-radius:12px}.ui-input--shadow input{box-shadow:0 6px 25px -6px rgba(0,0,0,var(--ui-shadow-opacity))}.ui-input--shadow input:focus{box-shadow:0 0 3px 0 rgba(0,0,0,var(--ui-shadow-opacity));transform:translate(0,3px)}.ui-input--shadow input:focus~.ui-input__icon{color:rgba(var(--ui-color),1);transform:translate(0,3px)}.ui-progress{width:90%;height:2px;margin-top:5px;margin-left:10px;border-radius:5px;background-color:rgba(var(--ui-grey-2),1)}.ui-progress__bar{width:0;height:2px;border-radius:inherit;background-color:rgba(var(--ui-color),1);box-shadow:-1px 2px 4px rgba(var(--ui-color),.25);transition:all .25s ease}.ui-input__icon{position:absolute;left:0;right:auto;width:36px;height:36px;display:flex;align-items:center;justify-content:center;transition:all .25s ease}.ui-input__icon.ui-input__icon--right{left:auto;right:0}.ui-input--icon input{padding-left:30px}.ui-input--icon input:focus{padding-left:35px}.ui-input--icon input:focus~.ui-input__label--placeholder{margin-left:13px}.ui-input--icon.ui-input--empty .ui-input__label--placeholder{margin-left:33px}.ui-input--icon-right input{padding-right:30px}.ui-input--state .ui-input__icon,.ui-input--state .ui-input__message{color:rgba(var(--ui-color),1)}.ui-input--state.ui-input--shadow input{box-shadow:0 6px 20px -6px rgba(var(--ui-color),.3)}.ui-input--state .ui-input__border{background-color:rgba(var(--ui-color),1)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$8 = undefined;
/* module identifier */

const __vue_module_identifier__$8 = undefined;
/* functional template */

const __vue_is_functional_template__$8 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$8 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$8,
  staticRenderFns: __vue_staticRenderFns__$8
}, __vue_inject_styles__$8, __vue_script__$8, __vue_scope_id__$8, __vue_is_functional_template__$8, __vue_module_identifier__$8, false, createInjector, undefined, undefined);

//
var script$7 = {
  name: 'ui-scroll',
  props: {
    direction: {
      type: String,
      default: 'xy'
    },
    triggers: {
      type: Array,
      default: null
    },
    snap: {
      type: String,
      default: null
    },
    snapAlign: {
      type: String,
      default: 'center'
    }
  },

  data() {
    const info = {
      position: {
        x: 0,
        y: 0
      },
      max: {
        x: 0,
        y: 0
      },
      ratio: {
        x: 0,
        y: 0
      }
    };

    if (this.triggers) {
      info.triggers = {};
    }

    return {
      info
    };
  },

  computed: {
    classes() {
      return ['ui-scroll', `ui-scroll--${this.direction}`, {
        'ui-scroll--snap': this.snap
      }];
    },

    style() {
      return {
        '--ui-snap': this.snap,
        '--ui-snap-align': this.snap && this.snapAlign
      };
    }

  },
  methods: {
    /**
     * Get current scroll info, such as position, ratio and max
     * @param {Element} [el] Viewport Node to update values
     * @returns {object} scroll info object
     */
    scrollInfo(el = null) {
      if (el) {
        const position = {
          x: Math.ceil(el.scrollLeft),
          y: Math.ceil(el.scrollTop)
        };
        const max = {
          x: Math.max(el.scrollWidth - el.clientWidth, 0),
          y: Math.max(el.scrollHeight - el.clientHeight, 0)
        };
        const ratio = {
          x: Math.min(1, position.x / max.x) || 0,
          y: Math.min(1, position.y / max.y) || 0
        };
        this.info = { ...this.info,
          position,
          max,
          ratio
        };
      }

      return this.info;
    },

    /**
     * Translate the value if needed of the respective axis
     * @param {string} axis x or y
     * @param {number|string} value the value to translate, e.g. 100, 100%, 50%
     * @returns {number} axis offset value
     */
    translate(axis, value) {
      if (/%$/.test(value)) {
        const ratio = Number(value.replace('%', '')) / 100;
        return Math.floor(this.info.max[axis] * ratio);
      }

      return value;
    },

    /**
     * Verify if the viewport is at a specific offset
     * @param {object} offset
     * @param {number|number[]|string|string[]} offset.x
     * @param {number|number[]|string|string[]} offset.y
     * @returns {boolean} whether viewport is on the offset
     */
    isAt(offset) {
      const {
        position,
        ratio
      } = this.info;
      return Object.entries(offset).every(([axis, at]) => {
        if (at === undefined) {
          return true;
        }

        if (Array.isArray(at)) {
          return at.every((value, index) => {
            const isRatio = /%$/.test(value);
            const metric = isRatio ? ratio[axis] : position[axis];

            if (isRatio) {
              const num = Number(value.replace('%', '')) / 100;

              if (index === 0) {
                return metric >= num;
              }

              return metric <= num;
            }

            return metric === value;
          });
        }

        const isRatio = /%$/.test(at);
        const metric = isRatio ? ratio[axis] : position[axis];
        const num = isRatio ? Number(at.replace('%', '')) / 100 : at;
        return metric === num;
      });
    },

    /**
     * Check whether a trigger matches the current viewport area
     * @param {object|string} trigger The name or an object with a trigger config
     * @param {boolean} [emit] Whether to emit trigger event in case state changed
     * @returns {boolean} whether state matches trigger config
     */
    check(trigger, emit = false) {
      if (typeof trigger === 'string') {
        if (trigger === 'all') {
          return this.triggers.map(data => this.check(data, emit)).every(result => result === true);
        }

        const data = this.triggers.find(({
          name
        }) => name === trigger);

        if (!data) {
          throw new Error(`ui-scroller: ${trigger} not found.`);
        }

        return this.check(data, emit);
      }

      const {
        name,
        x,
        y,
        on = 'in'
      } = trigger;
      const state = this.info.triggers[name];
      const triggered = this.isAt({
        x,
        y
      });
      const changed = state !== triggered;
      this.$set(this.info.triggers, name, triggered);

      if (changed) {
        if (emit) {
          if (on === 'in' && !state && triggered) {
            this.$emit(`${name}`, {
              state: 'in'
            });
          } else if (on === 'out' && state && !triggered) {
            this.$emit(`${name}`, {
              state: 'out'
            });
          } else if (on === 'in-out') {
            this.$emit(name, {
              state: triggered ? 'in' : 'out'
            });
          }
        }

        this.$emit('change', this.info.triggers);
      }

      return triggered;
    },

    /**
     * Scroll the viewport to a certain position/element.
     * @param {Object|String|Element} [offset] The offset/element to scroll to
     * @param {Number} options.left The X position to scroll to
     * @param {Number} options.top The Y position to scroll to
     * @param {Object} [options] ScrollToOptions
     * @returns {Promise} promise that will resolve when the scroll animation finishes
     */
    scroll(offset = null, options = {}) {
      const info = this.scrollInfo();

      if (!offset) {
        return info;
      }

      const {
        viewport: el
      } = this.$refs;
      let {
        left = offset.x,
        top = offset.y
      } = offset;
      let target = offset;

      if (typeof target === 'string') {
        target = el.querySelector(offset);

        if (!target) {
          return Promise.reject(new Error(`'.ui-scroll ${target}' not found.`));
        }
      }

      if (target instanceof Element) {
        left = target.offsetLeft - el.offsetLeft;
        top = target.offsetTop - el.offsetTop;
      } else {
        left = this.translate('x', left);
        top = this.translate('y', top);
      }

      if (left >= info.max.x) {
        left = info.max.x - 1;
      }

      if (top >= info.max.y) {
        top = info.max.y - 1;
      }

      if (this.direction === 'x') {
        top = undefined;
      } else if (this.direction === 'y') {
        left = undefined;
      }

      return new Promise(resolve => {
        const {
          fallback = 800
        } = options;

        if (target instanceof Element) {
          const {
            inline = 'nearest',
            block = 'nearest',
            behavior = 'smooth',
            threshold = 0.9
          } = options; // Ref https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer

          let observer = new IntersectionObserver(([{
            isIntersecting
          }]) => {
            if (isIntersecting) {
              observer.disconnect();
              observer = null;
              resolve();
            }
          }, {
            root: el,
            rootMargin: '0px',
            threshold
          });
          observer.observe(target); // Ref https://developer.mozilla.org/en-US/docs/Web/API/Element/scrollIntoView

          target.scrollIntoView({
            inline,
            block,
            behavior,
            ...options
          });
          setTimeout(() => {
            observer.disconnect();
            observer = null;
            resolve();
          }, fallback);
        } else {
          const {
            behavior = 'smooth'
          } = options;
          let requestId; // Ref https://developer.mozilla.org/en-US/docs/Web/API/Element/scroll

          el.scroll({
            left,
            top,
            behavior
          });

          const check = () => {
            const {
              scrollLeft,
              scrollTop
            } = el;
            let scrolled = false;

            if (this.direction === 'xy') {
              scrolled = scrollLeft >= (left || 0) && scrollTop >= (top || 0);
            } else if (this.direction === 'x') {
              scrolled = scrollLeft >= left;
            } else if (this.direction === 'y') {
              scrolled = scrollTop >= top;
            }

            if (scrolled) {
              resolve();
            } else {
              requestId = window.requestAnimationFrame(check);
            }
          }; // Ref https://developer.mozilla.org/en-US/docs/Web/API/window/requestAnimationFrame


          requestId = window.requestAnimationFrame(check);
          setTimeout(() => {
            // Ref https://developer.mozilla.org/en-US/docs/Web/API/window/cancelAnimationFrame
            window.cancelAnimationFrame(requestId);
            resolve();
          }, fallback);
        }
      });
    },

    onScroll: throttle( // eslint-disable-next-line func-names
    function ($event) {
      this.scrollInfo($event.target);

      if (this.triggers) {
        this.check('all', true);
      }
    }, 300, {
      trailing: true
    })
  },

  mounted() {
    this.scrollInfo(this.$refs.viewport);

    if (this.triggers) {
      this.check('all', false);
    }
  }

};

/* script */
const __vue_script__$7 = script$7;
/* template */

var __vue_render__$7 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.classes,
    style: _vm.style
  }, [_c('div', {
    ref: "viewport",
    staticClass: "ui-scroll__viewport",
    on: {
      "&scroll": function ($event) {
        return _vm.onScroll.apply(null, arguments);
      }
    }
  }, [_vm._t("default")], 2)]);
};

var __vue_staticRenderFns__$7 = [];
/* style */

const __vue_inject_styles__$7 = function (inject) {
  if (!inject) return;
  inject("data-v-98174fcc_0", {
    source: ".ui-scroll{overflow:hidden;position:relative}.ui-scroll__viewport{position:inherit;width:100%;height:100%;display:flex;overscroll-behavior:contain}.ui-scroll--x .ui-scroll__viewport{overflow:auto hidden;flex-direction:row}.ui-scroll--y .ui-scroll__viewport{overflow:hidden auto;flex-direction:column}.ui-scroll--xy .ui-scroll__viewport{overflow:auto}.ui-scroll--snap.ui-scroll--x .ui-scroll__viewport{scroll-snap-type:x var(--ui-snap);flex-direction:row}.ui-scroll--snap.ui-scroll--y .ui-scroll__viewport{scroll-snap-type:y var(--ui-snap);flex-direction:column}.ui-scroll--snap.ui-scroll--xy .ui-scroll__viewport{scroll-snap-type:both var(--ui-snap)}.ui-scroll--snap .ui-scroll__viewport>*{flex:0 0 100%;box-sizing:border-box;scroll-snap-align:var(--ui-snap-align)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$7 = undefined;
/* module identifier */

const __vue_module_identifier__$7 = undefined;
/* functional template */

const __vue_is_functional_template__$7 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$7 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$7,
  staticRenderFns: __vue_staticRenderFns__$7
}, __vue_inject_styles__$7, __vue_script__$7, __vue_scope_id__$7, __vue_is_functional_template__$7, __vue_module_identifier__$7, false, createInjector, undefined, undefined);

//
//
//
//
//
//
var script$6 = {
  name: 'ui-list'
};

/* script */
const __vue_script__$6 = script$6;
/* template */

var __vue_render__$6 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "vs-list"
  }, [_vm._t("default")], 2);
};

var __vue_staticRenderFns__$6 = [];
/* style */

const __vue_inject_styles__$6 = undefined;
/* scoped */

const __vue_scope_id__$6 = undefined;
/* module identifier */

const __vue_module_identifier__$6 = undefined;
/* functional template */

const __vue_is_functional_template__$6 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$6 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$6,
  staticRenderFns: __vue_staticRenderFns__$6
}, __vue_inject_styles__$6, __vue_script__$6, __vue_scope_id__$6, __vue_is_functional_template__$6, __vue_module_identifier__$6, false, undefined, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script$5 = {
  name: 'ui-list-item',
  props: {
    vsAvatar: {
      type: [Boolean, String],
      default: false
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    iconPack: {
      type: String,
      default: 'material-icons'
    }
  }
};

/* script */
const __vue_script__$5 = script$5;
/* template */

var __vue_render__$5 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "vs-list--item"
  }, [_vm.$slots.avatar ? _c('div', {
    staticClass: "vs-list--avatar"
  }, [_vm._t("avatar")], 2) : _vm._e(), _vm._v(" "), _vm.icon ? _c('div', {
    staticClass: "vs-list--icon"
  }, [_c('vs-icon', {
    attrs: {
      "icon-pack": _vm.iconPack,
      "icon": _vm.icon
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "list-titles w-full"
  }, [_vm.title ? _c('div', {
    staticClass: "vs-list--title"
  }, [_vm._v(_vm._s(_vm.title))]) : _vm._t("title"), _vm._v(" "), _vm.subtitle ? _c('div', {
    staticClass: "vs-list--subtitle"
  }, [_vm._v(_vm._s(_vm.subtitle))]) : _vm._t("subtitle")], 2), _vm._v(" "), _c('div', {
    staticClass: "vs-list--slot"
  }, [_vm._t("default")], 2)]);
};

var __vue_staticRenderFns__$5 = [];
/* style */

const __vue_inject_styles__$5 = undefined;
/* scoped */

const __vue_scope_id__$5 = undefined;
/* module identifier */

const __vue_module_identifier__$5 = undefined;
/* functional template */

const __vue_is_functional_template__$5 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$5 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$5,
  staticRenderFns: __vue_staticRenderFns__$5
}, __vue_inject_styles__$5, __vue_script__$5, __vue_scope_id__$5, __vue_is_functional_template__$5, __vue_module_identifier__$5, false, undefined, undefined, undefined);

//
var script$4 = {
  name: 'ui-list-header',
  props: {
    color: {
      type: String,
      default: 'primary'
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    iconPack: {
      type: String,
      default: 'material-icons'
    }
  },
  computed: {
    styleHeader() {
      return {
        color: colors.getColor(this.color)
      };
    }

  }
};

/* script */
const __vue_script__$4 = script$4;
/* template */

var __vue_render__$4 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "vs-list--header",
    class: ["vs-header-list-" + _vm.color, {
      'with-icon': _vm.icon
    }],
    style: _vm.styleHeader
  }, [_vm.icon ? _c('div', {
    staticClass: "vs-list--icon"
  }, [_c('vs-icon', {
    attrs: {
      "icon-pack": _vm.iconPack,
      "icon": _vm.icon
    }
  })], 1) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "list-titles"
  }, [_vm.title ? _c('div', {
    staticClass: "vs-list--title"
  }, [_vm._v(_vm._s(_vm.title))]) : _vm._e()])]);
};

var __vue_staticRenderFns__$4 = [];
/* style */

const __vue_inject_styles__$4 = undefined;
/* scoped */

const __vue_scope_id__$4 = undefined;
/* module identifier */

const __vue_module_identifier__$4 = undefined;
/* functional template */

const __vue_is_functional_template__$4 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$4 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$4,
  staticRenderFns: __vue_staticRenderFns__$4
}, __vue_inject_styles__$4, __vue_script__$4, __vue_scope_id__$4, __vue_is_functional_template__$4, __vue_module_identifier__$4, false, undefined, undefined, undefined);

//
var script$3 = {
  name: 'ui-loading',
  props: {
    active: {
      type: Boolean,
      default: true
    },
    text: {
      type: String,
      default: null
    },
    theme: {
      type: String,
      default: 'default'
    },
    background: {
      type: String,
      default: null
    },
    opacity: {
      type: Number,
      default: null
    },
    blurred: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: null
    },
    color: {
      type: String,
      default: 'primary'
    },
    scale: {
      type: Number,
      default: null
    },
    zIndex: {
      type: Number,
      default: null
    }
  },
  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-loader', `ui-loader--${this.theme}`, {
        'ui-loader--blurred': this.blurred,
        [`ui-loader--${this.color}`]: this.colorExists
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-background': colors.get(this.background),
        '--ui-background-opacity': this.opacity,
        '--ui-size': this.size,
        '--ui-color': color,
        '--ui-scale': this.scale,
        'z-index': this.zIndex
      };
    }

  }
};

/* script */
const __vue_script__$3 = script$3;
/* template */

var __vue_render__$3 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('transition', {
    attrs: {
      "name": "fade",
      "appear": ""
    }
  }, [_vm.active ? _c('div', {
    class: _vm.classes,
    style: _vm.style
  }, [_c('div', {
    staticClass: "ui-loader__effect"
  }), _vm._v(" "), _vm.text ? _c('div', {
    staticClass: "ui-loader__text"
  }, [_vm._t("default", function () {
    return [_vm._v("\n        " + _vm._s(_vm.text) + "\n      ")];
  })], 2) : _vm._e()]) : _vm._e()]);
};

var __vue_staticRenderFns__$3 = [];
/* style */

const __vue_inject_styles__$3 = function (inject) {
  if (!inject) return;
  inject("data-v-6d97a646_0", {
    source: ".ui-loader{position:absolute;top:0;left:0;width:100%;min-height:100%;display:flex;justify-content:center;align-items:center;flex-direction:column;background-color:rgba(var(--ui-background),var(--ui-background-opacity,.7));border-radius:inherit;pointer-events:none}body>.ui-loader{position:fixed;pointer-events:all}.ui-loader__container{position:relative;border-radius:inherit}.ui-loader__effect{position:relative;box-sizing:border-box;display:flex;align-items:center;justify-content:center;width:var(--ui-size,40px);height:var(--ui-size,40px);transform:scale(var(--ui-scale,1))}.ui-loader__effect:after,.ui-loader__effect:before{content:\"\";position:absolute;display:block;width:100%;height:100%;border-radius:inherit;box-sizing:border-box}.ui-loader__text{margin-top:7px;font-weight:600;font-size:.75em;color:rgba(var(--ui-color),1)}.ui-loader--blurred{backdrop-filter:blur(3px)}.ui-loader--default .ui-loader__effect{border-radius:100%}.ui-loader--default .ui-loader__effect:before{border:3px solid transparent;border-bottom-style:dashed;border-bottom-color:rgba(var(--ui-color),.35);animation:rotate .7s linear infinite}.ui-loader--default .ui-loader__effect:after{border:3px solid transparent;border-bottom-color:rgba(var(--ui-color),1);animation:rotate .7s ease infinite}.ui-loader--gradient .ui-loader__effect{border-radius:100%}.ui-loader--gradient .ui-loader__effect:before{background-image:linear-gradient(0deg,rgba(var(--ui-background),0) 33%,rgba(var(--ui-color),1) 100%);animation:rotate 1s linear infinite}.ui-loader--gradient .ui-loader__effect:after{top:2px;width:calc(100% - 3px);height:calc(100% - 3px);background-color:rgba(var(--ui-background),1);border-radius:100%}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$3 = undefined;
/* module identifier */

const __vue_module_identifier__$3 = undefined;
/* functional template */

const __vue_is_functional_template__$3 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$3 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$3,
  staticRenderFns: __vue_staticRenderFns__$3
}, __vue_inject_styles__$3, __vue_script__$3, __vue_scope_id__$3, __vue_is_functional_template__$3, __vue_module_identifier__$3, false, createInjector, undefined, undefined);

//
var script$2 = {
  name: 'ui-loader',
  components: {
    UiLoading: __vue_component__$3
  },
  inheritAttrs: false
};

/* script */
const __vue_script__$2 = script$2;
/* template */

var __vue_render__$2 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    staticClass: "ui-loader__container"
  }, [_vm._t("default"), _vm._v(" "), _c('ui-loading', _vm._b({}, 'ui-loading', _vm.$attrs, false))], 2);
};

var __vue_staticRenderFns__$2 = [];
/* style */

const __vue_inject_styles__$2 = undefined;
/* scoped */

const __vue_scope_id__$2 = undefined;
/* module identifier */

const __vue_module_identifier__$2 = undefined;
/* functional template */

const __vue_is_functional_template__$2 = false;
/* style inject */

/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$2 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$2,
  staticRenderFns: __vue_staticRenderFns__$2
}, __vue_inject_styles__$2, __vue_script__$2, __vue_scope_id__$2, __vue_is_functional_template__$2, __vue_module_identifier__$2, false, undefined, undefined, undefined);

//
var script$1 = {
  name: 'ui-pagination',
  props: {
    value: {
      type: Number,
      required: true
    },
    length: {
      type: Number,
      required: true
    },
    limit: {
      type: Number,
      default: 10
    },
    max: {
      type: Number,
      default: 10
    },
    startIndex: {
      type: Number
    },
    endIndex: {
      type: Number
    },
    arrows: {
      type: Boolean,
      default: true
    },
    numbers: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: [Boolean, Array],
      default: false
    },
    color: {
      type: String,
      default: 'primary'
    },
    size: {
      type: String,
      default: 'md'
    },
    await: {
      type: Function,
      default: null
    },
    loading: {
      type: Array,
      default: () => []
    }
  },

  data() {
    return {
      offset: 0,
      awaiting: null
    };
  },

  computed: {
    colorExists() {
      return colors.exists(this.color);
    },

    classes() {
      return ['ui-pagination', `ui-pagination--${this.size}`, {
        'ui-pagination--disabled': this.disabled === true,
        [`ui-pagination--${this.color}`]: this.colorExists
      }];
    },

    style() {
      let color = null;

      if (!this.colorExists) {
        color = colors.get(this.color);
      }

      return {
        '--ui-color': color
      };
    },

    page: {
      get() {
        return this.value;
      },

      set(value) {
        this.$emit('input', value);
      }

    },

    pages() {
      return Math.ceil(this.length / this.limit);
    },

    near() {
      const {
        pages,
        max
      } = this;
      let {
        page
      } = this;
      let near;
      const before = Math.floor(max / 2);
      const after = Math.ceil(max / 2) - 1;

      if (page < 1) {
        page = 1;
      } else if (page > pages) {
        page = pages;
      }

      if (page <= before) {
        near = 'start';
      } else if (page + after >= pages) {
        near = 'end';
      } else {
        near = 'middle';
      }

      return near;
    },

    between() {
      const {
        pages,
        max,
        near
      } = this;
      let start;
      let end;

      if (pages <= max) {
        start = 1;
        end = pages;
      } else if (near === 'start') {
        start = 1;
        end = max;
      } else if (near === 'end') {
        start = pages - max + 1;
        end = pages;
      } else {
        const before = Math.floor(max / 2);
        const after = Math.ceil(max / 2) - 1;
        let {
          page
        } = this;

        if (page < 1) {
          page = 1;
        } else if (page > pages) {
          page = pages;
        }

        start = page - before;
        end = page + after;
      }

      return {
        start,
        end
      };
    },

    index() {
      const {
        page,
        length,
        limit
      } = this;
      const startIndex = (page - 1) * limit;
      const endIndex = Math.min(startIndex + limit, length);
      return {
        startIndex,
        endIndex
      };
    },

    pagesRange() {
      if (this.length === 0) {
        return [1];
      }

      const {
        start,
        end
      } = this.between;
      return this.range(start, end);
    },

    slotData() {
      const {
        length,
        limit,
        pages,
        near,
        between: {
          start,
          end
        },
        index: {
          startIndex,
          endIndex
        }
      } = this;
      return {
        length,
        limit,
        pages,
        near,
        start,
        startIndex,
        end,
        endIndex
      };
    },

    disabledPages() {
      if (!Array.isArray(this.disabled)) {
        return [];
      }

      return this.disabled;
    },

    loadingPages() {
      const loading = this.loading.slice();

      if (this.awaiting) {
        loading.push(this.awaiting);
      }

      return loading;
    },

    skip() {
      return [...this.disabledPages, ...this.loadingPages];
    },

    previousDisabled() {
      if (this.page === 1) {
        return true;
      }

      if (this.skip.length > 0) {
        const before = this.range(1, this.page - 1);
        return before.every(n => this.skip.includes(n));
      }

      return false;
    },

    nextDisabled() {
      if (this.page === this.pages) {
        return true;
      }

      if (this.skip.length > 0) {
        const after = this.range(this.page + 1, this.pages);
        return after.every(n => this.skip.includes(n));
      }

      return false;
    }

  },
  watch: {
    length(l) {
      if (l === 0 || this.page > this.pages) {
        this.page = 1;
      }
    },

    page: {
      immediate: true,

      handler() {
        if (this.numbers) {
          this.$nextTick(() => {
            this.updateOffset();
          });
        }
      }

    },
    index: {
      immediate: true,

      handler({
        startIndex,
        endIndex
      }) {
        this.$emit('update:startIndex', startIndex);
        this.$emit('update:endIndex', endIndex);
      }

    }
  },
  methods: {
    updateOffset() {
      const el = this.$el.querySelector('.ui-pagination__page--active');
      this.offset = `${el.offsetLeft}px`;
    },

    range(start, end) {
      return Array(end - start + 1).fill().map((n, index) => start + index);
    },

    go(n) {
      if (this.disabledPages.includes(n)) {
        return;
      }

      if (typeof this.await === 'function') {
        this.awaiting = n;
        this.await(n).then(() => {
          this.page = n;
        }).catch(() => {
          this.awaiting = null;
        }).finally(() => {
          this.awaiting = null;
        });
      } else {
        this.page = n;
      }
    },

    previous(n = this.page - 1) {
      if (n < 1) {
        return;
      }

      if (this.skip.includes(n)) {
        this.previous(n - 1);
      } else {
        this.go(n);
      }
    },

    next(n = this.page + 1) {
      if (n > this.pages) {
        return;
      }

      if (this.skip.includes(n)) {
        this.previous(n + 1);
      } else {
        this.go(n);
      }
    }

  }
};

/* script */
const __vue_script__$1 = script$1;
/* template */

var __vue_render__$1 = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('div', {
    class: _vm.classes,
    style: _vm.style
  }, [_vm.arrows ? _c('button', {
    staticClass: "ui-pagination__page ui-pagination__page--previous",
    attrs: {
      "type": "button",
      "disabled": _vm.previousDisabled
    },
    on: {
      "click": function ($event) {
        return _vm.previous();
      }
    }
  }, [_vm._t("arrow:left", function () {
    return [_c('i', {
      staticClass: "bx bx-chevron-left"
    })];
  })], 2) : _vm._e(), _vm._v(" "), _c('div', {
    staticClass: "ui-pagination__pages"
  }, [_vm._t("default", function () {
    return [_vm.numbers ? [_vm._l(_vm.pagesRange, function (n) {
      return _c('button', {
        key: n,
        staticClass: "ui-pagination__page",
        class: {
          'ui-pagination__page--active': _vm.page === n,
          'ui-pagination__page--loading': _vm.loadingPages.includes(n)
        },
        attrs: {
          "type": "button",
          "disabled": _vm.disabledPages.includes(n)
        },
        on: {
          "click": function ($event) {
            return _vm.go(n);
          }
        }
      }, [_vm._v("\n          " + _vm._s(n) + "\n\n          "), _vm.loadingPages.includes(n) ? _c('ui-loading', {
        attrs: {
          "color": _vm.color,
          "scale": 0.8
        }
      }) : _vm._e()], 1);
    }), _vm._v(" "), _c('button', {
      key: "active-" + _vm.page,
      staticClass: "ui-pagination__page ui-pagination__active",
      style: {
        left: _vm.offset
      },
      attrs: {
        "type": "button"
      }
    }, [_vm._v("\n          " + _vm._s(_vm.page) + "\n        ")])] : _vm._e()];
  }, {
    "go": _vm.go
  }, _vm.slotData)], 2), _vm._v(" "), _vm.arrows ? _c('button', {
    staticClass: "ui-pagination__page ui-pagination__page--next",
    attrs: {
      "type": "button",
      "disabled": _vm.nextDisabled
    },
    on: {
      "click": function ($event) {
        return _vm.next();
      }
    }
  }, [_vm._t("arrow:right", function () {
    return [_c('i', {
      staticClass: "bx bx-chevron-right"
    })];
  })], 2) : _vm._e()]);
};

var __vue_staticRenderFns__$1 = [];
/* style */

const __vue_inject_styles__$1 = function (inject) {
  if (!inject) return;
  inject("data-v-0a634030_0", {
    source: ".ui-pagination{display:flex;flex-direction:row;align-items:center;justify-content:center;position:relative;transition:all .25s ease}.ui-pagination--disabled{opacity:.4;user-select:none;pointer-events:none}.ui-pagination--xs{--ui-size:23px}.ui-pagination--xs .ui-pagination__page{border-radius:8px;font-size:.7rem}.ui-pagination--sm{--ui-size:28px}.ui-pagination--sm .ui-pagination__page{border-radius:10px;font-size:.8rem}.ui-pagination--md{--ui-size:32px}.ui-pagination--md .ui-pagination__page{border-radius:12px;font-size:.9rem}.ui-pagination--md .ui-pagination__page i{font-size:1rem}.ui-pagination--lg{--ui-size:40px}.ui-pagination--lg .ui-pagination__page{border-radius:14px;font-size:1rem}.ui-pagination--lg .ui-pagination__page i{font-size:1.3rem}.ui-pagination--xl{--ui-size:50px}.ui-pagination--xl .ui-pagination__page{border-radius:16px;font-size:1.1rem}.ui-pagination--xl .ui-pagination__page i{font-size:1.5rem}.ui-pagination__page{appearance:none;border:0;padding:0;margin:0 2px;width:var(--ui-size);height:var(--ui-size);font-size:.9rem;color:rgba(var(--ui-text),1);background-color:rgba(var(--ui-grey-3),1);box-sizing:border-box;transition:all .25s ease;user-select:none;display:inline-flex;justify-content:center;align-items:center;position:relative}.ui-pagination__page:hover{background-color:rgba(var(--ui-grey-4),1)}.ui-pagination__page:active{transform:scale(.9)}.ui-pagination__page:disabled{opacity:.4;pointer-events:none}.ui-pagination__page.ui-pagination__active{--ui-text:255,255,255;position:absolute;top:0;background-color:rgba(var(--ui-color),1);box-shadow:0 5px 20px 0 rgba(var(--ui-color),.3);pointer-events:none;margin:0;animation:zoom-sm .5s forwards}.ui-pagination__page--loading{cursor:pointer;pointer-events:none;border-radius:100%}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__$1 = undefined;
/* module identifier */

const __vue_module_identifier__$1 = undefined;
/* functional template */

const __vue_is_functional_template__$1 = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__$1 = /*#__PURE__*/normalizeComponent({
  render: __vue_render__$1,
  staticRenderFns: __vue_staticRenderFns__$1
}, __vue_inject_styles__$1, __vue_script__$1, __vue_scope_id__$1, __vue_is_functional_template__$1, __vue_module_identifier__$1, false, createInjector, undefined, undefined);

//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
//
var script = {
  name: 'ui-dialog',
  props: {
    target: {
      type: Element,
      default: () => document.body
    },
    value: {
      type: Boolean,
      default: true
    },
    title: {
      type: String,
      default: null
    },
    text: {
      type: String,
      default: null
    },
    icon: {
      type: String,
      default: null
    },
    width: {
      type: String,
      default: 'auto'
    },
    closable: {
      type: Boolean,
      default: true
    },
    animation: {
      type: String,
      default: 'rebound'
    },
    blurred: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    classes() {
      return ['ui-dialog', {
        'ui-dialog--blurred': this.blurred
      }];
    },

    style() {
      let {
        width
      } = this;

      if (width === 'auto') {
        width = null;
      }

      return {
        width
      };
    }

  },
  methods: {
    esc(e) {
      if (e.which === 27) {
        this.close();
      }
    },

    close(e = null) {
      if ((e === null || e === void 0 ? void 0 : e.target) !== (e === null || e === void 0 ? void 0 : e.currentTarget)) {
        return;
      }

      if (this.closable) {
        this.$emit('input', false);
      } else {
        const {
          container
        } = this.$refs;
        const handler = {
          on: () => container.classList.add('rebound'),
          off: () => container.classList.remove('rebound')
        };
        handler.on();
        container.addEventListener('animationend', handler.off, {
          once: true,
          passive: true
        });
      }
    }

  },

  mounted() {
    window.addEventListener('keydown', this.esc, {
      passive: true
    });
    this.target.appendChild(this.$el);
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this.esc);
    this.$el.remove();
  }

};

/* script */
const __vue_script__ = script;
/* template */

var __vue_render__ = function () {
  var _vm = this;

  var _h = _vm.$createElement;

  var _c = _vm._self._c || _h;

  return _c('transition', {
    attrs: {
      "name": "ui-dialog",
      "appear": ""
    }
  }, [_vm.value ? _c('div', {
    class: _vm.classes,
    on: {
      "click": _vm.close
    }
  }, [_c('transition', {
    attrs: {
      "name": _vm.animation,
      "appear": ""
    }
  }, [_c('div', _vm._b({
    ref: "container",
    staticClass: "ui-dialog__container",
    style: _vm.style
  }, 'div', _vm.$attrs, false), [_vm._t("container", function () {
    return [_vm.title ? _c('header', {
      staticClass: "ui-dialog__title"
    }, [_vm._t("title", function () {
      return [_vm.icon ? _c('i', {
        staticClass: "ui-dialog__icon",
        class: _vm.icon
      }) : _vm._e(), _vm._v("\n\n              " + _vm._s(_vm.title) + "\n            ")];
    })], 2) : _vm._e(), _vm._v(" "), _c('div', {
      staticClass: "ui-dialog__content"
    }, [_vm._t("default", function () {
      return [_vm._v("\n              " + _vm._s(_vm.text) + "\n            ")];
    })], 2), _vm._v(" "), _c('footer', {
      staticClass: "ui-dialog__actions"
    }, [_vm._t("actions")], 2)];
  }), _vm._v(" "), _vm.closable ? _c('button', {
    staticClass: "ui-dialog__close",
    attrs: {
      "type": "button"
    },
    on: {
      "click": function ($event) {
        return _vm.$emit('input', false);
      }
    }
  }, [_c('i', {
    staticClass: "bx bx-x"
  })]) : _vm._e()], 2)])], 1) : _vm._e()]);
};

var __vue_staticRenderFns__ = [];
/* style */

const __vue_inject_styles__ = function (inject) {
  if (!inject) return;
  inject("data-v-92eab54a_0", {
    source: ".ui-dialog{--ui-backdrop:0,0,0;display:flex;flex-direction:column;justify-content:center;align-items:center;position:absolute;top:0;left:0;width:100vw;height:100vh;z-index:99999;background-color:rgba(var(--ui-backdrop),var(--ui-background-opacity,.2));color:rgb(var(--ui-text))}.ui-dialog--blurred{backdrop-filter:saturate(1.8) blur(13px)}body>.ui-dialog{position:fixed}.ui-dialog__container{border-radius:20px;background:#fff;box-shadow:0 5px 30px 0 rgba(var(--ui-backdrop),var(--ui-shadow-opacity));min-width:400px;max-width:calc(100vw - 40px);transition:all .25s ease;position:relative}.ui-dialog__container.rebound{animation:rebound-click .3s}.ui-dialog__close{appearance:none;position:absolute;top:-6px;right:-6px;color:rgb(var(--ui-text));border:0;background:inherit;border-radius:12px;padding:5px 6px;font-size:20px;box-shadow:0 5px 20px 0 rgba(0,0,0,var(--ui-shadow-opacity));transition:all .25s ease}.ui-dialog__close i{opacity:.7;transition:all .25s ease}.ui-dialog__close:hover{transform:translate(-2px,2px);box-shadow:0 0 4px 0 rgba(0,0,0,var(--ui-shadow-opacity))}.ui-dialog__close:hover i{opacity:1}.ui-dialog__title{font-size:1.05rem;font-weight:500;padding:20px 16px 10px;display:flex;align-items:center}.ui-dialog__icon{font-size:1.2rem;margin-right:.5rem}.ui-dialog__content{padding:16px;font-size:.9rem}.ui-dialog__actions{display:flex;flex-direction:row;justify-content:flex-end;padding:0 10px 10px}.ui-dialog__actions .ui-button__content{padding:10px 20px}.ui-dialog__actions .ui-button~.ui-button{margin-left:10px}.ui-dialog-enter-active,.ui-dialog-leave-active{transition:all .25s ease}.ui-dialog-enter-active .ui-dialog__container,.ui-dialog-leave-active .ui-dialog__container{transition:all .25s ease}.ui-dialog-enter,.ui-dialog-leave-to{opacity:0}.ui-dialog-enter .ui-dialog__container,.ui-dialog-leave-to .ui-dialog__container{transform:scale(.7)}",
    map: undefined,
    media: undefined
  });
};
/* scoped */


const __vue_scope_id__ = undefined;
/* module identifier */

const __vue_module_identifier__ = undefined;
/* functional template */

const __vue_is_functional_template__ = false;
/* style inject SSR */

/* style inject shadow dom */

const __vue_component__ = /*#__PURE__*/normalizeComponent({
  render: __vue_render__,
  staticRenderFns: __vue_staticRenderFns__
}, __vue_inject_styles__, __vue_script__, __vue_scope_id__, __vue_is_functional_template__, __vue_module_identifier__, false, createInjector, undefined, undefined);

var components = /*#__PURE__*/Object.freeze({
  __proto__: null,
  UiButton: __vue_component__$e,
  UiButtonGroup: __vue_component__$d,
  UiTable: __vue_component__$c,
  UiCheckbox: __vue_component__$b,
  UiTippy: __vue_component__$a,
  UiTooltip: __vue_component__$9,
  UiInput: __vue_component__$8,
  UiScroll: __vue_component__$7,
  UiList: __vue_component__$6,
  UiListItem: __vue_component__$5,
  UiListHeader: __vue_component__$4,
  UiLoader: __vue_component__$2,
  UiLoading: __vue_component__$3,
  UiPagination: __vue_component__$1,
  UiDialog: __vue_component__
});

function loader(props = {}, $parent = null) {
  let {
    target = document.body
  } = props;

  if (typeof target === 'string') {
    target = document.body.querySelector(target);
  }

  if (!target) {
    throw new Error('ui-loader: target not found.');
  }

  const propsData = { ...props,
    target
  };

  if (target !== document.body) {
    const {
      position
    } = window.getComputedStyle(target);

    if (position === 'static') {
      target.style.position = 'relative';
    }

    if (!props.size) {
      propsData.size = `${target.clientHeight}px`;
    }
  }

  const Loader = Vue.extend(__vue_component__$3);
  let vm = new Loader({
    propsData,
    methods: {
      destroy() {
        this.active = false;
        this.$nextTick(() => {
          this.$destroy();
        });
      }

    },

    destroyed() {
      this.$el.remove();
    }

  });
  vm.$mount();
  target.appendChild(vm.$el);

  if ($parent) {
    $parent.$on('hook:beforeDestroy', () => {
      vm.$destroy();
      vm = null;
    });
  }

  return vm;
}

function dialog({
  $parent,
  ...props
}) {
  let {
    target = document.body
  } = props;

  if (typeof target === 'string') {
    target = document.body.querySelector(target);
  }

  if (!target) {
    throw new Error('ui-dialog: target not found.');
  }

  const propsData = { ...props,
    target
  };

  if (target !== document.body) {
    const {
      position
    } = window.getComputedStyle(target);

    if (position === 'static') {
      target.style.position = 'relative';
    }
  }

  const Dialog = Vue.extend(__vue_component__);
  let vm = new Dialog({
    propsData,
    methods: {
      destroy() {
        this.$emit('input', false);
        this.$nextTick(() => {
          this.$destroy();
        });
      }

    }
  });
  vm.$on('input', value => {
    if (!value) {
      vm.destroy();
    }
  });
  vm.$mount();

  if ($parent) {
    $parent.$on('hook:beforeDestroy', () => {
      vm.$destroy();
      vm = null;
    });
  }

  return vm;
}

var functions = {
  loader,
  dialog
};

/* eslint-disable no-underscore-dangle */

const install = function installVoltUI(Vue) {
  Object.values(components).forEach(component => {
    Vue.component(component.name, component);
  }); // eslint-disable-next-line no-param-reassign

  Vue.prototype.$ui = functions;
}; // Create module definition for Vue.use()

export default install;
export { __vue_component__$e as UiButton, __vue_component__$d as UiButtonGroup, __vue_component__$b as UiCheckbox, __vue_component__ as UiDialog, __vue_component__$8 as UiInput, __vue_component__$6 as UiList, __vue_component__$4 as UiListHeader, __vue_component__$5 as UiListItem, __vue_component__$2 as UiLoader, __vue_component__$3 as UiLoading, __vue_component__$1 as UiPagination, __vue_component__$7 as UiScroll, __vue_component__$c as UiTable, __vue_component__$a as UiTippy, __vue_component__$9 as UiTooltip };
